export type EventListener<P> = (payload: P) => void;

/**
 * Typed publish/subscribe bus. Systems communicate gameplay facts
 * ("player jumped", "bottle collected") without knowing each other –
 * audio, effects, UI and analytics simply subscribe.
 */
export class EventBus<Events extends object> {
  private readonly listeners = new Map<keyof Events, Set<EventListener<never>>>();

  on<K extends keyof Events>(type: K, listener: EventListener<Events[K]>): () => void {
    let set = this.listeners.get(type);
    if (!set) {
      set = new Set();
      this.listeners.set(type, set);
    }
    set.add(listener as EventListener<never>);
    return () => this.off(type, listener);
  }

  off<K extends keyof Events>(type: K, listener: EventListener<Events[K]>): void {
    this.listeners.get(type)?.delete(listener as EventListener<never>);
  }

  emit<K extends keyof Events>(type: K, payload: Events[K]): void {
    const set = this.listeners.get(type);
    if (!set) return;
    for (const listener of set) (listener as EventListener<Events[K]>)(payload);
  }

  clear(): void {
    this.listeners.clear();
  }
}
